"use client";

import { createContext, useContext, useEffect, useMemo, useRef } from "react";

import { shallowEqual } from "react-redux";

import districtsApi, {
  useGetDistrictLevelsQuery,
} from "@/providers/store/api/districts/districts";
import type { SchoolLevelType } from "@/providers/store/api/schools/schools.interface";
import { useAppSelector } from "@/providers/store/hooks";

import type {
  SchoolLevelsContextValue,
  SchoolLevelsProviderProps,
} from "./school-levels-context.types";

const SchoolLevelsContext = createContext<SchoolLevelsContextValue | null>(null);

const EMPTY_SCHOOL_LEVELS_CONTEXT: SchoolLevelsContextValue = {
  schoolLevels: undefined,
  districtSchoolLevelTypes: new Set(),
  isLoading: false,
  fulfilledTimeStamp: undefined,
  prevFulfilledTimeStamp: undefined,
};

export function useSchoolLevels() {
  const ctx = useContext(SchoolLevelsContext);
  if (!ctx) throw new Error("useSchoolLevels must be used inside <SchoolLevelsProvider />");
  return ctx;
}

export function useSchoolLevelsOptional(): SchoolLevelsContextValue {
  const ctx = useContext(SchoolLevelsContext);
  return ctx ?? EMPTY_SCHOOL_LEVELS_CONTEXT;
}

export function SchoolLevelsProvider({ children }: SchoolLevelsProviderProps) {
  const selectedDistrictId = useAppSelector(
    (s) => s.district.selectedDistrict?.id ?? null,
    shallowEqual,
  );

  const prevDistrictIdRef = useRef<string | null>(selectedDistrictId);

  const {
    data: schoolLevels,
    isLoading,
  } = useGetDistrictLevelsQuery(
    { districtId: selectedDistrictId ?? "" },
    { skip: !selectedDistrictId || selectedDistrictId === "" },
  );

  const { fulfilledTimeStamp } =
    districtsApi.endpoints.getDistrictLevels.useQueryState(
      { districtId: selectedDistrictId ?? "" },
      { skip: !selectedDistrictId || selectedDistrictId === "" },
    );

  const { fulfilledTimeStamp: prevFulfilledTimeStamp } =
    districtsApi.endpoints.getDistrictLevels.useQueryState(
      { districtId: prevDistrictIdRef.current ?? "" },
      { skip: !prevDistrictIdRef.current || prevDistrictIdRef.current === "" },
    );

  useEffect(() => {
    prevDistrictIdRef.current = selectedDistrictId;
  }, [selectedDistrictId]);

  const districtSchoolLevelTypes = useMemo<ReadonlySet<SchoolLevelType>>(
    () => new Set(schoolLevels?.map((l) => l.type) ?? []),
    [schoolLevels],
  );

  const contextValue: SchoolLevelsContextValue = {
    schoolLevels,
    districtSchoolLevelTypes,
    isLoading,
    fulfilledTimeStamp,
    prevFulfilledTimeStamp,
  };

  return (
    <SchoolLevelsContext.Provider value={contextValue}>
      {children}
    </SchoolLevelsContext.Provider>
  );
}
